import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../data/supabaseClient';
import { AuthContext } from '../Provider';
import Step1Username from './Step1Username';
import Step2PersonalInfo from './Step2PersonalInfo';
import Step3CarDetails from './Step3CarDetails';
import Spinner from '../common/Spinner';
import FloatingOrbs from '../FloatingOrbs';
import { logProfileError, logProfileAction, logProfilePerformance } from '../../utils/errorLogger';
import './ProfileComplete.css';

function ProfileCompleteContainer() {
  const { t } = useTranslation('common');
  const { refreshProfile } = useContext(AuthContext);
  const [currentStep, setCurrentStep] = useState(1);
  const [userData, setUserData] = useState(null);
  const [step1Data, setStep1Data] = useState(null); // Store step 1 data for country
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startTime] = useState(Date.now()); // Track total completion time
  const navigate = useNavigate();

  // Container ref for mouse tracking and orbs
  const containerRef = useRef(null);
  const gridRef = useRef(null);
  
  // Mouse position tracking for grid animation
  const [gridTransform, setGridTransform] = useState({ x: 0, y: 0 });

  // Grid mouse movement handler (same as login page)
  useEffect(() => {
    let animationFrameId = null;

    const handleMouseMove = (e) => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);

      animationFrameId = requestAnimationFrame(() => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          const centerX = rect.width / 2;
          const centerY = rect.height / 2;
          const xRatio = (e.clientX - rect.left - centerX) / centerX;
          const yRatio = (e.clientY - rect.top - centerY) / centerY;

          // Grid movement - subtle effect
          setGridTransform({
            x: xRatio * 5,
            y: yRatio * 5
          });
        }
      });
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      return () => {
        container.removeEventListener('mousemove', handleMouseMove);
        if (animationFrameId) cancelAnimationFrame(animationFrameId);
      };
    }
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get authenticated session
        const { data: sessionData, error: authError } = await supabase.auth.getSession();
        
        if (authError) {
          throw authError;
        }

        if (!sessionData.session) {
          navigate('/login', { replace: true });
          return;
        }

        const user = sessionData.session.user;
        console.log('ProfileComplete: User authenticated:', user.id);

        // For new users, check if they have a profile first before fetching
        // This prevents unnecessary 406 errors
        let profile = null;
        try {
          console.log('Checking if profile exists for user:', user.id);
          const { data: profileData, error: profileError, count } = await supabase
            .from('profiles')
            .select('id, completion_step, username, language, city, avatar_url, first_name, last_name, year_of_birth, gender', { count: 'exact' })
            .eq('id', user.id);

          if (profileError) {
            console.error('Profile fetch error:', profileError);
            // Continue with null profile - this might be a new user
            profile = null;
          } else if (profileData && profileData.length > 0) {
            profile = profileData[0];
            console.log('Profile found:', profile);
          } else {
            console.log('No profile found - this is normal for new users');
            profile = null;
          }
        } catch (error) {
          console.log('Profile check failed - assuming new user:', error.message);
          profile = null;
        }

        // Phone now lives in the RLS-protected profiles_private table; fetch it separately
        if (profile) {
          try {
            const { data: privData } = await supabase
              .from('profiles_private')
              .select('phone')
              .eq('id', user.id)
              .maybeSingle();
            if (privData?.phone) profile.phone = privData.phone;
          } catch (privErr) {
            console.log('Could not fetch private phone:', privErr.message);
          }
        }

        // Combine user and profile data
        setUserData({ ...user, profile: profile || {} });

        // Check completion status and redirect accordingly
        if (profile?.completion_step !== null && profile?.completion_step !== undefined && profile.completion_step >= 3) {
          console.log('✅ Profile complete (step', profile.completion_step, '), redirecting to home');
          navigate('/home', { replace: true });
          return;
        }

        // Set current step based on profile (default to 1 for new users)
        // Handle completion_step = 0 by starting at step 1, but preserve the database value
        let startStep;
        if (profile?.completion_step === 0) {
          startStep = 1; // UI starts at step 1, but profile completion_step remains 0
        } else {
          startStep = profile?.completion_step ?? 1;
        }
        setCurrentStep(startStep);
        
        console.log('🚀 Profile setup starting at step:', startStep);
        console.log('📊 Profile state:', {
          hasProfile: !!profile,
          completionStep: profile?.completion_step,
          hasUsername: !!profile?.username,
          hasLocation: !!profile?.city
        });

      } catch (error) {
        console.error('Error:', error.message);
        logProfileError(error, 'container', { action: 'session_and_profile_fetch' });
        setError(error.message || t('profile_setup.error_generic'));
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate, t]);

  const handleStepComplete = async (stepData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Store step 1 data for use in step 2
      if (currentStep === 1) {
        setStep1Data(stepData);
        console.log('💾 Step 1 data stored:', stepData);
      }
      
      if (!userData?.id) {
        throw new Error(t('profile_setup.error_no_user'));
      }

      const nextStep = currentStep + 1;

      // phone is stored in the RLS-protected profiles_private table, not profiles
      const { phone: stepPhone, ...profileStepData } = stepData;

      // Check if profile exists, create or update accordingly
      if (!userData.profile || Object.keys(userData.profile).length === 0) {
        // Create new profile for first-time users
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: userData.id,
            email: userData.email,
            ...profileStepData,
            completion_step: nextStep,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertError) {
          throw insertError;
        }
        
        console.log('Profile created successfully for step:', currentStep);
      } else {
        // Update existing profile
        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            ...profileStepData,
            completion_step: nextStep,
            updated_at: new Date().toISOString()
          })
          .eq('id', userData.id);

        if (updateError) {
          throw updateError;
        }

        console.log('Profile updated successfully for step:', currentStep);
      }

      // Persist phone to the RLS-protected private table (upsert creates the row if needed)
      if (stepPhone !== undefined) {
        const { error: phoneError } = await supabase
          .from('profiles_private')
          .upsert({ id: userData.id, phone: stepPhone || null, updated_at: new Date().toISOString() });
        if (phoneError) throw phoneError;
      }

      // Update local user data
      setUserData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          ...stepData,
          completion_step: nextStep
        }
      }));

      if (nextStep <= 3) {
        setCurrentStep(nextStep);
        logProfileAction('step_advanced', `step${currentStep}`, { fromStep: currentStep, toStep: nextStep });
      } else {
        console.log('Profile setup complete, refreshing AuthContext and redirecting to home');
        
        // Refresh the user profile in AuthContext so country filtering works
        try {
          await refreshProfile();
          console.log('✅ AuthContext refreshed after profile completion');
          
          // Add small delay to ensure context re-renders before navigation
          await new Promise(resolve => setTimeout(resolve, 300));
          console.log('✅ Context refresh completed, navigating to home');
        } catch (error) {
          console.warn('⚠️ Could not refresh AuthContext:', error);
          // Continue anyway - non-critical
        }
        
        const totalTime = Date.now() - startTime;
        logProfilePerformance('total_setup_time', totalTime, 'complete');
        logProfileAction('profile_setup_completed', 'complete', { totalTime, stepsCompleted: 3 });
        navigate('/home', { replace: true });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      logProfileError(error, `step${currentStep}`, {
        action: 'profile_update',
        step: currentStep,
        dataKeys: Object.keys(stepData)
      });
      setError(error.message || t('profile_setup.error_update'));
      
      // If it's a constraint violation (duplicate username), provide specific feedback
      if (error.message && error.message.includes('duplicate key value violates unique constraint')) {
        if (error.message.includes('username')) {
          setError(t('errors.username_taken'));
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!userData?.id) {
        throw new Error(t('profile_setup.error_no_user'));
      }

      const nextStep = currentStep + 1;

      // Check if profile exists, create or update accordingly
      if (!userData.profile || Object.keys(userData.profile).length === 0) {
        // Create minimal profile for first-time users
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: userData.id,
            email: userData.email,
            completion_step: nextStep,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertError) {
          throw insertError;
        }
        
        console.log('Minimal profile created when skipping step:', currentStep);
      } else {
        // Update only the completion_step
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ 
            completion_step: nextStep,
            updated_at: new Date().toISOString()
          })
          .eq('id', userData.id);

        if (updateError) {
          throw updateError;
        }
        
        console.log('Profile step skipped:', currentStep);
      }

      // Update local user data
      setUserData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          completion_step: nextStep
        }
      }));

      if (nextStep <= 3) {
        setCurrentStep(nextStep);
      } else {
        navigate('/home', { replace: true });
      }
    } catch (error) {
      console.error('Error in handleSkip:', error);
      setError(error.message || t('profile_setup.error_skip'));
    } finally {
      setLoading(false);
    }
  };

  // Handle error retry
  const handleRetry = () => {
    setError(null);
    if (userData) {
      // If we have user data, just retry current step
      setLoading(false);
    } else {
      // Otherwise reload the page to restart the flow
      window.location.reload();
    }
  };

  // If loading, show spinner
  if (loading) {
    return (
      <div className="pcf-loading">
        <Spinner size="large" customText={t('profile_setup.loading')} />
      </div>
    );
  }

  // If error, show error message with retry button
  if (error) {
    return (
      <div className="pcf-error">
        <h2>{t('profile_setup.error_title')}</h2>
        <p>{error}</p>
        <button 
          className="pcf-retry-button" 
          onClick={handleRetry}
        >
          {t('buttons.retry')}
        </button>
      </div>
    );
  }

  // Main render - Enhanced with login page styling
  return (
    <div ref={containerRef} className="enhanced-profile-complete-page">
      {/* Animated background grid */}
      <div className="profile-animated-bg-grid">
        <div 
          ref={gridRef}
          className="profile-grid-pattern" 
          style={{
            transform: `translate(${gridTransform.x}px, ${gridTransform.y}px) scale(${1 + Math.abs(gridTransform.x + gridTransform.y) * 0.001})`
          }}
        />
      </div>

      {/* Profile Complete Logo */}
      <div className="profile-complete-logo-container">
        <h1 className="profile-complete-logo">
          <span className="profile-logo-text" data-text="Complete Profile">Complete Profile</span>
          <div className="profile-logo-glow" />
        </h1>
      </div>

      {/* Profile form container with orbs */}
      <div className="profile-form-container">
        {/* Physics-based floating orbs - 4 larger orbs with low cursor attraction */}
        <FloatingOrbs 
          containerRef={containerRef}
          orbCount={4}
          minSize={150}
          maxSize={300}
          cursorAttraction="low"
        />
        
        <div className="pcf-container">
          <div className="pcf-step-indicator">
            <div className="pcf-step-progress">
              <div className={`pcf-step-bullet ${currentStep >= 1 ? 'pcf-active' : ''}`}>1</div>
              <div className={`pcf-step-line ${currentStep >= 2 ? 'pcf-active' : ''}`}></div>
              <div className={`pcf-step-bullet ${currentStep >= 2 ? 'pcf-active' : ''}`}>2</div>
              <div className={`pcf-step-line ${currentStep >= 3 ? 'pcf-active' : ''}`}></div>
              <div className={`pcf-step-bullet ${currentStep >= 3 ? 'pcf-active' : ''}`}>3</div>
            </div>
            <p className="pcf-step-description">
              {currentStep === 1 && t('profile_setup.step1_description')}
              {currentStep === 2 && t('profile_setup.step2_description')}
              {currentStep === 3 && t('profile_setup.step3_description')}
            </p>
          </div>

          {userData ? (
            <>
              {currentStep === 1 && (
                <Step1Username
                  userData={userData}
                  onComplete={handleStepComplete}
                />
              )}

              {currentStep === 2 && (
                <Step2PersonalInfo
                  userData={{ ...userData, step1Data }} // Pass step1Data
                  onComplete={handleStepComplete}
                />
              )}

              {currentStep === 3 && (
                <Step3CarDetails
                  userData={userData}
                  onComplete={handleStepComplete}
                  onSkip={handleSkip}
                />
              )}
            </>
          ) : (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <p>Loading profile data...</p>
              <Spinner size="medium" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfileCompleteContainer;